// ─────────────────────────────────────────────────────────────
// Measurement Protocol — EXPANDED MP ROW (per-MP tab container)
//
// Moved out of the original monolithic MPCreationIndex.js into its own
// file. Two behavior fixes were made here (UI is unchanged):
//
// 1. LAZY TAB LOADING — previously all 5 tabs were mounted at once
//    (just hidden with `display:none`), so every tab's data-fetching
//    effect fired the instant a row was expanded, regardless of which
//    tab the user actually wanted. Now a tab only mounts (and fetches
//    its data) the first time it's clicked. Once visited it stays
//    mounted (hidden, not unmounted) so flipping back to a
//    already-visited tab is instant and does NOT re-fetch — this
//    keeps tab switching smooth while still satisfying "only fetch
//    the record for the tab you clicked, otherwise don't fetch".
//
// 2. GLOBAL LOADING + NOTIFY ON SUBMIT — the cross-tab Submit (Save
//    All) now also drives the app-wide loading overlay
//    (useGlobalLoader, the same one other pages already use) so the
//    whole panel visibly shows "working…" instead of only disabling
//    one button, and still shows the existing success/error toast
//    when done.
// ─────────────────────────────────────────────────────────────
import { useState, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { TABS, Toast, EmailPreviewModal } from "./shared/mpShared";
import TestInfoTab from "./tabs/TestInfoTab";
import MeasurementChainTab from "./tabs/MeasurementChainTab";
import DUTChainTab from "./tabs/DUTChainTab";
import CommunicationResultTab from "./tabs/CommunicationResultTab";
import QuestionnairesTab from "./tabs/QuestionnairesTab";
import { companyID, employeeID } from "../../Common/UIComponents";
import { useGlobalLoader } from "../../Common/GlobalLoader";
import { MP_MPSubmitAll } from "../../API_Service/MeasurementProtocol_Slice";
import { flushSync } from "react-dom";

function MPExpandedRow({ mp, onSaved }) {
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useGlobalLoader();
  const [activeTab, setActiveTab] = useState("testinfo");
  // Which tabs have been clicked at least once. Only a visited tab is
  // mounted, so only a visited tab's fetch effect ever runs. The
  // default active tab ("testinfo") is pre-visited so it loads its
  // data immediately on expand, exactly like before.
  const [visitedTabs, setVisitedTabs] = useState({ testinfo: true });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, msg: "", type: "success" });
  const [overallPreviewOpen, setOverallPreviewOpen] = useState(false);

  const testInfoRef = useRef(null);
  const mchainRef = useRef(null);
  const dchainRef = useRef(null);
  const commRef = useRef(null);
  const questRef = useRef(null);

  const tabRefs = useMemo(
    () => ({
      testinfo: testInfoRef,
      mchain: mchainRef,
      dchain: dchainRef,
      comm: commRef,
      quest: questRef,
    }),
    [],
  );

  const notify = (msg, type = "success") => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast((t) => ({ ...t, show: false })), 3000);
  };

  const handleTabClick = (key) => {
    setActiveTab(key);
    setVisitedTabs((v) => (v[key] ? v : { ...v, [key]: true }));
  };

  // Cross-tab Submit: validates every tab (in tab order) before saving
  // anything. On the first invalid tab, switches to it and focuses the
  // missing field so the user can fix it immediately.
  const handleSubmitAll = async () => {
    try {
      const previouslyVisited = visitedTabs;
      const newlyVisitedKeys = TABS.filter(
        (tab) => !previouslyVisited[tab.key],
      ).map((tab) => tab.key);

      if (newlyVisitedKeys.length) {
        flushSync(() => {
          setVisitedTabs((v) => {
            const all = { ...v };
            TABS.forEach((tab) => {
              all[tab.key] = true;
            });
            return all;
          });
        });

        // Wait only for the tabs we just force-mounted, and only as
        // long as their fetch actually takes.
        await Promise.all(
          newlyVisitedKeys.map((key) => {
            const inst = tabRefs[key]?.current;
            return inst?.ready ?? Promise.resolve();
          }),
        );
      }

      for (const tab of TABS) {
        const inst = tabRefs[tab.key]?.current;
        // Should always be mounted at this point (every tab was just
        // force-visited above), but guard anyway just in case a tab
        // fails to attach a ref for some reason.
        if (!inst) {
          handleTabClick(tab.key);
          notify(
            `The "${tab.label}" tab isn't ready yet. Please open it and try Submit again.`,
            "error",
          );
          return;
        }
        let result;
        try {
          result = inst.validate ? inst.validate() : { valid: true };
        } catch (err) {
          console.error(`Validation error in "${tab.label}" tab:`, err);
          // Fail open on an unexpected validation bug rather than
          // permanently blocking Submit — but log it loudly for devs.
          result = { valid: true };
        }
        if (!result?.valid) {
          handleTabClick(tab.key);
          notify(
            `Please complete the required fields in the "${tab.label}" tab.`,
            "error",
          );
          setTimeout(() => inst.focusField?.(result.field), 100);
          return;
        }
      }

      setSubmitting(true);
      showLoader();
      for (const tab of TABS) {
        const inst = tabRefs[tab.key]?.current;
        if (inst?.save) {
          // eslint-disable-next-line no-await-in-loop
          await inst.save();
        }
      }

      const payload = {
        mpid: mp?.MPID,
        modifiedBy: employeeID,
        companyID: companyID,
      };
      dispatch(MP_MPSubmitAll(payload)).then((res) => {
        if (res.payload.data > 0) {
          notify("All sections submitted successfully!");
          onSaved?.();
        } else {
          notify(
            "Something went wrong while submitting. Please try again.",
            "error",
          );
        }
      });
    } catch (err) {
      console.error("Error submitting Measurement Protocol:", err);
      notify(
        "Something went wrong while submitting. Please try again.",
        "error",
      );
    } finally {
      setSubmitting(false);
      hideLoader();
    }
  };

  const getCombinedPreviewRows = () => {
    const rows = [];
    try {
      TABS.forEach((tab) => {
        const inst = tabRefs[tab.key]?.current;
        const tabRows = inst?.getPreviewRows ? inst.getPreviewRows() : [];
        if (tabRows?.length) {
          rows.push({ label: `— ${tab.label} —`, value: "" });
          rows.push(...tabRows);
        }
      });
    } catch (err) {
      console.error("Error building combined email preview:", err);
    }
    return rows;
  };

  return (
    <div
      className="w-full max-w-full min-w-0 border-b border-[#e5e9e9]"
      style={{ background: "#eef4f4" }}
    >
      <Toast msg={toast.msg} show={toast.show} type={toast.type} />
      <EmailPreviewModal
        open={overallPreviewOpen}
        onClose={() => setOverallPreviewOpen(false)}
        mp={mp}
        subject={`Overall MP Update — ${mp?.MPNo ?? "New MP"}`}
        rows={getCombinedPreviewRows()}
      />

      <div className="flex items-stretch border-b border-[#e5e9e9] bg-white overflow-x-auto">
        <div className="flex flex-1 overflow-x-auto">
          {TABS?.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabClick(tab.key)}
              className={`px-5 py-3 text-xs font-bold whitespace-nowrap border-b-2 transition-all
                ${
                  activeTab === tab.key
                    ? "border-[#08757C] text-[#08757C] bg-[#f0fafa]"
                    : "border-transparent text-[#6e797a] hover:text-[#08757C] hover:bg-[#f7fafa]"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-5 min-w-0">
        {/* Each tab only mounts (and therefore only fetches its data)
            once it has actually been clicked. Already-visited tabs stay
            mounted but hidden so switching back to them is instant and
            doesn't re-fetch. */}
        <div style={{ display: activeTab === "testinfo" ? "block" : "none" }}>
          {visitedTabs.testinfo && (
            <TestInfoTab
              ref={testInfoRef}
              mpID={mp.MPID}
              entry={mp}
              mp={mp}
              onSaved={onSaved}
              onSubmitAll={handleSubmitAll}
            />
          )}
        </div>
        <div style={{ display: activeTab === "mchain" ? "block" : "none" }}>
          {visitedTabs.mchain && (
            <MeasurementChainTab
              ref={mchainRef}
              mpID={mp.MPID}
              mp={mp}
              onSaved={onSaved}
              onSubmitAll={handleSubmitAll}
            />
          )}
        </div>
        <div style={{ display: activeTab === "dchain" ? "block" : "none" }}>
          {visitedTabs.dchain && (
            <DUTChainTab
              ref={dchainRef}
              mpID={mp.MPID}
              mp={mp}
              onSaved={onSaved}
              onSubmitAll={handleSubmitAll}
            />
          )}
        </div>
        <div style={{ display: activeTab === "comm" ? "block" : "none" }}>
          {visitedTabs.comm && (
            <CommunicationResultTab
              ref={commRef}
              mpID={mp.MPID}
              mp={mp}
              resultList={mp?.ResultList ?? []}
              onSaved={onSaved}
              onSubmitAll={handleSubmitAll}
            />
          )}
        </div>
        <div style={{ display: activeTab === "quest" ? "block" : "none" }}>
          {visitedTabs.quest && (
            <QuestionnairesTab
              ref={questRef}
              mpID={mp.MPID}
              mp={mp}
              onSaved={onSaved}
              onSubmitAll={handleSubmitAll}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default MPExpandedRow;
export { MPExpandedRow };
